import { Injectable, TemplateRef } from '@angular/core';
import { NgbToast } from '@ng-bootstrap/ng-bootstrap';

export interface ToastAction {
  label: string;
  action: () => void;
  style?: 'primary' | 'secondary' | 'success' | 'danger';
}

export interface ToastInfo {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  actions?: ToastAction[];
  delay?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  toasts: ToastInfo[] = [];

  constructor() {}

  success(title: string, message: string, actions?: ToastAction[]): void {
    this.show(title, message, 'success', actions);
  }

  error(title: string, message: string, actions?: ToastAction[]): void {
    this.show(title, message, 'error', actions);
  }

  warning(title: string, message: string, actions?: ToastAction[]): void {
    this.show(title, message, 'warning', actions);
  }

  info(title: string, message: string, actions?: ToastAction[]): void {
    this.show(title, message, 'info', actions);
  }

  private show(title: string, message: string, type: 'success' | 'error' | 'warning' | 'info', actions?: ToastAction[]): void {
    const toast: ToastInfo = {
      id: this.generateId(),
      title,
      message,
      type,
      actions,
      delay: actions && actions.length > 0 ? 0 : 3000 // Don't auto-hide if has actions
    };

    this.toasts.push(toast);
  }

  remove(toastId: string): void {
    this.toasts = this.toasts.filter(toast => toast.id !== toastId);
  }

  executeAction(action: ToastAction, toastId: string): void {
    action.action();
    // Remove toast after action execution
    setTimeout(() => {
      this.remove(toastId);
    }, 100);
  }

  getIconClass(type: string): string {
    switch (type) {
      case 'success': return 'fas fa-check-circle text-success';
      case 'error': return 'fas fa-exclamation-circle text-danger';
      case 'warning': return 'fas fa-exclamation-triangle text-warning';
      case 'info': return 'fas fa-info-circle text-info';
      default: return 'fas fa-info-circle text-info';
    }
  }

  getBgClass(type: string): string {
    switch (type) {
      case 'success': return 'bg-success';
      case 'error': return 'bg-danger';
      case 'warning': return 'bg-warning';
      case 'info': return 'bg-info';
      default: return 'bg-light';
    }
  }

  getButtonClass(style?: string): string {
    switch (style) {
      case 'primary': return 'btn-primary';
      case 'secondary': return 'btn-secondary';
      case 'success': return 'btn-success';
      case 'danger': return 'btn-danger';
      default: return 'btn-primary';
    }
  }

  private generateId(): string {
    return 'toast-' + Math.random().toString(36).substr(2, 9);
  }
}
